/**
 * Tracks editable AG Grid state against an immutable baseline.
 * This keeps track of edits, validity, usages of current values and dependent actions.
 * It ensures all of these work efficiently together and keeps this abstracted from the main grid component.
 *
 * Edits:
 * - Stores only meaningful row deltas; reverting to baseline removes the diff.
 * - Treats blank-equivalent values (null, undefined, '') as equal for diffing.
 * - Tracks row and column additions/removals separately from cell edits.
 *
 * Validations:
 * - Maintains cell, row, and grid issue state with block/error/warn counts.
 * - Runs row-local validation for isolated edits and full-grid validation for cross-row fields.
 * - Exposes grid- and row-level validation queries without leaking count storage to callers.
 *
 * Index counts:
 * - Keeps live non-blank value indexes for counts, uniqueness, and duplicate checks.
 * - This lets users cheaply ask get counts, uniqueness, duplicates, distinct values, matching row IDs etc.
 * - Supports multi-field lookups by intersecting indexed row-id sets.
 *
 * Reactions:
 * - Applies field-driven derived changes before edits are persisted to tracker state.
 * - Ensures derived values participate in indexing, validation, and AG Grid updates.
 * - Debounces full-grid validation so dense changes remain performant.
 */

// Disabled here so we can define the trackerAPI in convenient place
/* eslint-disable no-use-before-define */

import { reactive } from 'vue';
import severity, { severityOrder } from '@/validation/severity';
import {
  get,
  setWith,
  unset,
  has,
  isEqual,
  isEmpty,
  isNil,
  keyBy,
  cloneDeep,
  each,
  size,
  keys,
  values,
  flatMap,
  uniq,
  debounce,
  toPath,
  mapValues,
  map,
  reduce,
  sortBy,
  merge,
  isPlainObject,
} from 'lodash';

function isBlank(value) {
  return value === null || value === undefined || value === '';
}

// Values we never count (blanks shouldn't duplicate each other).
function isCountable(value) {
  return !isBlank(value);
}

// Equality check for change detection. Treats all blank-equivalent values
// (null / undefined / '') as identical to each other so that a silent
// is blank. For non-blank values, delegates to deep isEqual.
function equalForGridDiff(a, b) {
  if (isBlank(a) && isBlank(b)) return true;
  return isEqual(a, b);
}

function countKey(value) {
  return String(value);
}

// Recursively unsets nested paths. i.e. { a: { b: { c: 5 }}} -> {}
// This keeps edits minimal - all contents are meaningful updates and no empty paths.
function removePath(obj, path) {
  if (!has(obj, path)) return;
  unset(obj, path);
  if (path.length <= 1) return;
  const parentPath = path.slice(0, -1);
  if (isEmpty(get(obj, parentPath))) {
    removePath(obj, parentPath);
  }
}

// Sets nested paths on objects automatically. i.e. {} -> { a: { b: { c: 5 }}}
function setPath(obj, path, value) {
  setWith(obj, path, value, Object);
}

function fieldPath(rowId, field) {
  return [rowId, ...toPath(field)];
}

export function useGridTracker() {
  const emptyIssueCounts = () => mapValues(severity, () => 0);

  // Immutable snapshot of original data, keyed by rowId.
  // Deliberately non-reactive: nothing in the UI reacts to it.
  // Absolutely must not be modified by anything outside of init.
  let baseline = {};

  // Indexes of field values at init time. Also non-reactive.
  // Must not be modified by anything outside of init.
  let initialIndexes = {};

  // Non-reactive validation functions supplied by the grid's domain.
  let validator = null;

  // True when the caller passed an explicit indexedFields list to init().
  // When false (auto-inferred mode), edits to fields not yet in indexedFields
  // are lazily registered. When true, the explicit allowlist is respected.
  let explicitIndexedFields = false;

  const state = reactive({
    rowId: 'productKey',
    edits: {},
    // Never write directly — always use setRowIssues().
    // { rowKey: { columnName: { errorLevel: [...validationIssueObjects ]}}}
    invalidCells: {},
    // Per-row severity tallies, kept in lockstep with invalidCells.
    // { rowKey: { block: 1, error: 0, warn: 5} }
    rowIssueCounts: {},
    // Grid severity tallies, kept in lockstep with invalidCells.
    // { block: 4, error: 3, warn: 20}
    issueCounts: emptyIssueCounts(),
    // Which rows have which values for which columns
    // Gives us counts and references to products with those counts
    // { columnName: { value1: set([...rowKeys]), value2: set([...differentRowKeys]) }}
    currentIndexes: {},
    // Which columns we index for currentIndexes
    // This defaults to all, for simplicity. This exists to allow subsetting if required.
    indexedFields: [],
    // These define "If X is modified, do Y". This lets you define things like:
    // If parentProduct is set to true, set assortment to false.
    reactions: {},
    // Ids of removed rows that exist in baseline (i.e. previously saved).
    // A row added and removed before it's saved never exists here.
    removedRows: {},
    // Field ids added since the last init(). Cleared on init.
    addedColumns: new Set(),
    // Field ids that existed at init() and have since been removed. Cleared on init.
    removedColumns: new Set(),
  });

  const trackerApi = {
    init,
    handleEdit,
    applyTransaction,
    addColumn,
    removeColumn,
    getAllRows,
    getChanges,
    getBaselineDisplayValue,
    getCellTooltip,
    hasChanges,
    hasDiff,
    count,
    getCounts,
    distinctValues,
    isUnique,
    valueExists,
    getRowIdsWhere,
    getRowsWhere,
    getRowsFromBaseline,
    getCellIssues,
    getHighestSeverity,
    isCellInSameRowInvalid,
    isGridInvalid,
    gridHasBlockingValidations,
    gridHasErrors,
    gridHasWarnings,
    gridHasFailedValidations,
    rowHasFailedValidations,
    countRowsWithFailedValidations,
    state,
  };

  // Helper, used to turn cell edit action into standard structured form.
  function parseParams(params) {
    const rowId = params.data[state.rowId];
    const { field } = params.colDef;
    return { rowId, field };
  }

  // Return the current value the tracker knows.
  // Sample use - find out what the current edited value is when queuing an edit.
  function getTrackedValue(rowId, field) {
    const path = fieldPath(rowId, field);
    const res = has(state.edits, path) ? get(state.edits, path) : get(baseline, path);
    return res;
  }

  function getBaselineDisplayValue(params) {
    const { rowId, field } = parseParams(params);
    if (isNil(rowId)) return undefined;

    const data = cloneDeep(baseline[rowId]);
    if (!data) return undefined;

    const { colDef } = params;
    const value = isNil(field) ? undefined : get(data, field);
    const displayParams = {
      ...params,
      data,
      node: params.node ? Object.assign(Object.create(params.node), { data }) : params.node,
      value,
      // Read raw fields from the baseline row. This does not resolve other column value getters.
      getValue: fieldName => get(data, fieldName),
    };
    const displayValue =
      typeof colDef.valueGetter === 'function' ? colDef.valueGetter(displayParams) : value;

    return typeof colDef.valueFormatter === 'function'
      ? colDef.valueFormatter({ ...displayParams, value: displayValue })
      : displayValue;
  }

  function getCellTooltip(
    params,
    { formatIssue = issue => issue.message, formatPreviousValue } = {}
  ) {
    const messages = (getCellIssues(params) ?? []).map(formatIssue);

    if (hasDiff(params) && typeof formatPreviousValue === 'function') {
      messages.push(formatPreviousValue(getBaselineDisplayValue(params)));
    }

    return messages.join('\n');
  }

  // Ensure new values/ columns get counted.
  function addToIndex(field, value, rowId) {
    if (!isCountable(value)) return;

    // First time we've ever seen this field.
    if (!state.indexedFields.includes(field)) {
      state.indexedFields.push(field);
    }

    const path = [field, countKey(value)];

    let ids = get(state.currentIndexes, path);

    if (!ids) {
      ids = new Set();
      setPath(state.currentIndexes, path, ids);
    }

    ids.add(rowId);
  }

  // Remove counts, i.e. when a column is dropped.
  function removeFromIndex(field, value, rowId) {
    if (!isCountable(value)) return;

    const path = [field, countKey(value)];

    const ids = get(state.currentIndexes, path);

    if (!ids) return;

    ids.delete(rowId);

    if (ids.size === 0) {
      removePath(state.currentIndexes, path);
    }
  }

  function applyCellChange(rowId, field, patch) {
    const path = fieldPath(rowId, field);
    const oldValue = getTrackedValue(rowId, field);

    // Object patches deep-merge onto the tracked value so a partial update
    // (e.g. { color: 'blue' }) doesn't drop sibling keys (e.g. size). Primitives
    // replace as before. merge({}, ...) writes into a fresh target so a baseline
    // reference in oldValue is never mutated — baseline stays frozen.
    const newValue =
      isPlainObject(patch) && isPlainObject(oldValue) ? merge({}, oldValue, patch) : patch;

    // Ensure meaningful change - i.e. not undefined -> ''
    if (equalForGridDiff(newValue, oldValue)) return false;

    // Update edits map (remove if reverted to baseline).
    // Unsaved rows have no baseline, so every field must stay in edits to be persisted.
    if (has(baseline, rowId) && equalForGridDiff(newValue, get(baseline, path))) {
      removePath(state.edits, path);
    } else {
      setPath(state.edits, path, newValue);
    }

    // Update counts for values.
    if (!explicitIndexedFields || state.indexedFields.includes(field)) {
      removeFromIndex(field, oldValue, rowId);
      addToIndex(field, newValue, rowId);
    }

    return true;
  }

  // ---- Reactions ---------------------------------------------------
  // These handle cases like "If parentProduct is modified, ensure parentProduct is false afterwards"
  // They are triggered on any edit, whether direct or from transactions.

  function applyFieldReactions(update, changedFields) {
    const derivedChanges = {};

    changedFields.forEach(field => {
      const reaction = state.reactions[field];
      if (!reaction) return;

      const changes = reaction(update);

      if (changes) {
        Object.assign(derivedChanges, changes);
      }
    });

    return {
      ...derivedChanges,
      ...update,
    };
  }

  // ---- indexes ----------------------------------------------------------
  // These tell you which rows have which values for which columns
  // Basically, like an inverted index for "What has organic = True?"
  // Used for things like uniqueness, counts, duplicates etc.

  function buildIndexes(rows, fields, rowIdField) {
    const indexes = {};

    each(rows, row => {
      const rowId = row[rowIdField];

      each(fields, field => {
        const value = get(row, field);

        if (!isCountable(value)) return;

        const path = [field, countKey(value)];

        const ids = get(indexes, path);

        if (ids) {
          ids.add(rowId);
        } else {
          setPath(indexes, path, new Set([rowId]));
        }
      });
    });

    return indexes;
  }

  // Checks index for "What rows have organic = true AND brand = coke"
  // Intersects those to find overlap, smallest first.
  function getRowIdsWhere(where) {
    if (isEmpty(where)) throw new Error('where must contain at least one predicate');

    const sets = sortBy(
      map(
        where,
        (value, field) => get(state.currentIndexes, [field, countKey(value)]) ?? new Set()
      ),
      'size'
    );

    return reduce(
      sets.slice(1),
      (result, set) => {
        const intersection = new Set();
        result.forEach(id => {
          if (set.has(id)) intersection.add(id);
        });
        return intersection;
      },
      new Set(sets[0])
    );
  }

  // Returns rows from baseline. Derives from initial state.
  // If you're deleting a saved row, it's still here. If you're checking a new, unsaved row - it isn't.
  function getRowsFromBaseline(rowKeys) {
    // IMPORTANT: This returns the original data - including deleted products.
    return [...rowKeys].map(key => ({ ...baseline[key] }));
  }

  // Get rows according to a given set of predicates like { organic: true, brand: 'coke' }
  function getRowsWhere(where) {
    const ids = getRowIdsWhere(where);
    return [...ids].map(id => state.gridApi?.getRowNode(String(id))?.data).filter(Boolean);
  }

  // Get raw count of a given value. Just counts the set of productKeys we've tracked for it.
  function count(where) {
    return getRowIdsWhere(where).size;
  }

  // Count, but plural.
  function getCounts(field) {
    const counts = get(state.currentIndexes, field, {});
    return mapValues(counts, size);
  }

  function isUnique(where) {
    return count(where) === 1;
  }

  function valueExists(where) {
    return count(where) > 0;
  }

  // ---- validation -------------------------------------------------------
  // Detailed error data is stored per rowKey -> column value
  // We want to avoid doing O(n) checks for "is there a critical error blocking save"
  // So we keep both row and grid-level aggregates that are updated whenever a validation changes.

  function countIssues(rowResult) {
    const counts = emptyIssueCounts();
    if (!rowResult) return counts;

    values(rowResult).forEach(cell => {
      severityOrder.forEach(level => {
        counts[level] += cell[level]?.length ?? 0;
      });
    });

    return counts;
  }

  function setRowIssues(rowId, result) {
    const prev = state.rowIssueCounts[rowId] ?? emptyIssueCounts();
    const next = countIssues(result);

    severityOrder.forEach(level => {
      state.issueCounts[level] += next[level] - prev[level];
    });

    if (result) {
      state.invalidCells[rowId] = result;
      state.rowIssueCounts[rowId] = next;
    } else {
      delete state.invalidCells[rowId];
      delete state.rowIssueCounts[rowId];
    }
  }

  function resetIssues() {
    state.invalidCells = {};
    state.rowIssueCounts = {};
    state.issueCounts = emptyIssueCounts();
  }

  function validateAllRows() {
    if (!validator) return;
    resetIssues();
    const rows = getAllRows();
    const results = validator.validateRows(rows);

    results.forEach(({ product, issues }) => {
      if (!state.removedRows[product[state.rowId]]) setRowIssues(product[state.rowId], issues);
    });
    state.gridApi?.refreshCells({ force: true });
  }

  function validateEditedRow(row) {
    if (validator) setRowIssues(row[state.rowId], validator.validateRow(row));
  }

  const scheduleGridValidation = debounce(() => {
    validateAllRows();
  }, 5);

  // Helper function, useful outside of tracker and within.
  function getAllRows() {
    if (!state.gridApi) return [];
    const rows = [];
    state.gridApi.forEachNode(node => rows.push(node.data));
    return rows;
  }

  // ---- lifecycle -------------------------------------------------------

  function init({
    data,
    validation = null,
    rowId = 'productKey',
    indexedFields,
    reactions = {},
    api = null,
  }) {
    // Clear any pending state in case of re-init. i.e. a discard.
    scheduleGridValidation.cancel();
    validator = validation;
    state.rowId = rowId;
    state.edits = {};
    state.removedRows = {};
    state.addedColumns = new Set();
    state.removedColumns = new Set();
    state.reactions = reactions;
    state.gridApi = api;
    resetIssues();

    // Baseline is the keyed initial state used for figuring out diffs.
    baseline = keyBy(cloneDeep(data), rowId);

    // Default to all fields present on the data.
    explicitIndexedFields = indexedFields !== undefined;
    state.indexedFields = indexedFields ?? uniq(flatMap(data, keys));

    // Get initial counts, so we can compute changes against them.
    initialIndexes = buildIndexes(values(baseline), state.indexedFields, state.rowId);

    state.currentIndexes = cloneDeep(initialIndexes);

    if (validator) {
      const results = validator.validateRows(data);
      results.forEach(({ product, issues }) => setRowIssues(product[state.rowId], issues));
    }
  }

  // Primary edit path
  function updateRow(update) {
    const rowId = update[state.rowId];

    if (state.removedRows[rowId] || (!has(baseline, rowId) && !has(state.edits, rowId))) {
      return null;
    }

    // Detect changed fields before reactions so reactions fire
    // only when their field actually differs from the current resolved value.
    const changedFields = [];
    each(update, (value, field) => {
      if (field === state.rowId) return;
      if (!equalForGridDiff(value, getTrackedValue(rowId, field))) {
        changedFields.push(field);
      }
    });

    const reactedUpdate = applyFieldReactions(update, changedFields);

    let changed = false;

    each(reactedUpdate, (value, field) => {
      if (field === state.rowId) return;

      if (applyCellChange(rowId, field, value)) changed = true;
    });

    return changed ? reactedUpdate : null;
  }

  function handleEdit(params) {
    const { rowId, field } = parseParams(params);

    const reactedUpdate = updateRow({
      [state.rowId]: rowId,
      [field]: params.newValue,
    });

    if (!reactedUpdate) return;

    Object.entries(reactedUpdate).forEach(([reactedField, value]) => {
      if (reactedField === state.rowId) return;

      params.node.setDataValue(reactedField, value);
    });

    validateEditedRow(merge({}, params.data, reactedUpdate));
    params.api.refreshCells({ rowNodes: [params.node], force: true });
    scheduleGridValidation();
  }

  function addRow(row) {
    const rowId = row[state.rowId];

    // No page currently supports re-adding removed rows.
    // As a result it is not handled here.
    // Currently supported path is to discard changes in that case.

    if (has(baseline, rowId) || has(state.edits, rowId)) {
      console.warn(`useGridTracker: ignored duplicate add for row "${rowId}"`);
      return;
    }

    // Update diff, counts and validations.
    each(row, (value, field) => {
      if (value === undefined) return;
      setPath(state.edits, fieldPath(rowId, field), value);
      if (!explicitIndexedFields || state.indexedFields.includes(field)) {
        addToIndex(field, value, rowId);
      }
    });
  }

  function removeRow(rowOrId) {
    const rowId = typeof rowOrId === 'object' ? rowOrId[state.rowId] : rowOrId;

    const isSaved = has(baseline, rowId);
    if (!isSaved && !has(state.edits, rowId)) return;

    // Remove from indexes using current values before dropping.
    each(state.indexedFields, field => {
      removeFromIndex(field, getTrackedValue(rowId, field), rowId);
    });

    unset(state.edits, rowId);
    setRowIssues(rowId, null);

    // Saved rows keep their baseline entry and are flagged as removed;
    // unsaved rows are simply cancelled, leaving zero state behind.
    if (isSaved) {
      state.removedRows[rowId] = true;
    }
  }

  // Takes partial update, expands to be full row data, applies the transaction.
  // Updates add/ update/ remove parameters if passed. Passes through other params unchanged.
  function applyTransaction({ add = [], update = [], remove = [], ...rest } = {}) {
    // Process removals first so they don't interfere with index lookups below.
    each(remove, removeRow);

    // Process adds.
    each(add, addRow);

    // Process partial updates: resolve reactions, materialise full AG Grid rows.
    const fullRowsForGrid = [];
    each(update, partialUpdate => {
      const rowId = partialUpdate[state.rowId];
      // Capture the current grid row BEFORE updateRow mutates tracker state,
      // so the materialized row reflects the pre-update grid state merged with
      // the reacted patch (including any reaction-derived field changes).
      const reactedPatch = updateRow(partialUpdate);
      const currentRow = state.gridApi?.getRowNode(String(rowId))?.data;
      if (!reactedPatch || !currentRow) return;
      // Use merge to preserve deeply nested values from updates, like storeClassEligibility
      fullRowsForGrid.push(merge({}, currentRow, reactedPatch));
    });

    // Apply updated parameters - pass through any others that were provided.
    if (state.gridApi && (remove.length || add.length || fullRowsForGrid.length)) {
      const gridTransaction = { ...rest };
      if (remove.length) gridTransaction.remove = remove;
      if (add.length) gridTransaction.add = add;
      if (fullRowsForGrid.length) gridTransaction.update = fullRowsForGrid;
      state.gridApi.applyTransaction(gridTransaction);
    }

    scheduleGridValidation();
  }

  // Get all edits. Used for powering save methods in grids.
  function getChanges() {
    return {
      edits: state.edits,
      removedRows: keys(state.removedRows),
      removedColumns: [...state.removedColumns],
      addedColumns: [...state.addedColumns],
    };
  }

  // Drives functionality like "should discard be enabled"
  function hasChanges() {
    return (
      !isEmpty(state.edits) ||
      !isEmpty(state.removedRows) ||
      state.removedColumns.size > 0 ||
      state.addedColumns.size > 0
    );
  }

  // Controls diff colouring
  function hasDiff(params) {
    const { rowId, field } = parseParams(params);
    if (isNil(field)) return false;
    const editExists = has(state.edits, fieldPath(rowId, field));
    return editExists;
  }

  // Some cells are blocked from editing if another field is invalid
  function isCellInSameRowInvalid(params, field) {
    const { rowId } = parseParams(params);

    const cell = state.invalidCells[rowId]?.[field];
    if (!cell) return false;

    return (cell[severity.block]?.length ?? 0) > 0 || (cell[severity.error]?.length ?? 0) > 0;
  }

  // Returns true when any block or error issue exists anywhere in the grid.
  // Warn-only grids are considered valid.
  function isGridInvalid() {
    return gridHasErrors();
  }

  function failedValidationsExist(validationSummary, level) {
    if (level === severity.block) return validationSummary?.[severity.block] > 0;
    if (level === severity.error) {
      return validationSummary?.[severity.block] > 0 || validationSummary?.[severity.error] > 0;
    }
    if (level === severity.warn) return validationSummary?.[severity.warn] > 0;
    return false;
  }

  function gridHasFailedValidations(level) {
    const gridValidationSummary = state.issueCounts;
    return failedValidationsExist(gridValidationSummary, level);
  }

  function rowHasFailedValidations(rowKey, level) {
    const rowValidationSummary = state.rowIssueCounts[rowKey];
    return failedValidationsExist(rowValidationSummary, level);
  }

  // Returns true when a block-severity validation exists anywhere in the grid (gates save).
  function gridHasBlockingValidations() {
    return gridHasFailedValidations(severity.block);
  }

  function gridHasErrors() {
    return gridHasFailedValidations(severity.error);
  }

  function gridHasWarnings() {
    return gridHasFailedValidations(severity.warn);
  }

  function countRowsWithFailedValidations(level) {
    return keys(state.rowIssueCounts).filter(rowKey => rowHasFailedValidations(rowKey, level))
      .length;
  }

  function distinctValues(field) {
    return keys(get(state.currentIndexes, field, {}));
  }

  // Returns a flat array of issues for a cell, optionally filtered by severity.
  // Order when no severity given: block → error → warn.
  // Returns null when there are no matching issues.
  function getCellIssues(params, level) {
    const { rowId, field } = parseParams(params);
    if (isNil(field)) return null;
    const cellEntry = state.invalidCells[rowId]?.[field];
    if (!cellEntry) return null;
    if (level) {
      const bucket = cellEntry[level];
      return bucket?.length > 0 ? bucket : null;
    }
    const flat = severityOrder.flatMap(s => cellEntry[s] ?? []);
    return flat.length > 0 ? flat : null;
  }

  // Returns the highest issue severity for a cell, or null when it has no issues.
  function getHighestSeverity(params) {
    const { rowId, field } = parseParams(params);
    if (isNil(field)) return null;
    const cellEntry = state.invalidCells[rowId]?.[field];
    if (!cellEntry) return null;
    return severityOrder.find(level => cellEntry[level]?.length > 0) ?? null;
  }

  // Adds a new column to the tracker. If indexed (default: true), the current
  // index for the field is built from the live grid rows.
  function addColumn(field, { indexed = true } = {}) {
    if (indexed) {
      if (state.indexedFields.includes(field)) {
        console.warn(`useGridTracker: addColumn called for already-indexed field "${field}"`);
      } else {
        state.indexedFields.push(field);
      }
      getAllRows().forEach(row => {
        addToIndex(field, get(row, field), row[state.rowId]);
      });
    }

    state.addedColumns.add(field);
    scheduleGridValidation();
  }

  // Removes a column from the tracker. Drop any state, remove rules.
  // Revalidate all rows as validation status may have changed.
  function removeColumn(field) {
    if (state.addedColumns.has(field)) {
      // Unsaved column — cancel it, leave no trace in removedColumns.
      state.addedColumns.delete(field);
    } else {
      // Baseline column — flag it for deletion on save.
      state.removedColumns.add(field);
    }

    const idx = state.indexedFields.indexOf(field);
    if (idx !== -1) state.indexedFields.splice(idx, 1);

    unset(state.currentIndexes, field);

    each(keys(state.edits), rowId => {
      removePath(state.edits, fieldPath(rowId, field));
    });

    validateAllRows();
  }

  return trackerApi;
}
